import {
  Award,
  ChevronDown,
  Bell,
  Heart,
  Info,
  Lock,
  LogOut,
  Menu,
  Package,
  PanelLeftClose,
  PanelLeftOpen,
  Plane,
  Shield,
  ShieldPlus,
  User,
  X,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  DESTINATIONS,
  EMERGENCY_PATH,
  destinationFor,
  isEmergencyPath,
  isUnder,
} from "@/config/travelerNav";
import { SyncStatusIndicator } from "../ui/SyncStatusIndicator";
import { syncTimeZone } from "../../services/api/nudgeApi";
import BottomTabBar from "./BottomTabBar";
import SectionTabs from "./SectionTabs";
import { useRole } from "../../hooks/useRole";
import {
  sectionBasePath,
  sectionLandingPath,
  sectionsForRoles,
} from "@/config/backOfficeNav";
import { rolesFromToken } from "@/utils/staffRoles";
import { useComDeskMenu } from "@/hooks/useComDeskMenu";
import { useAuthStore } from "../../stores/authStore";
import { useI18nStore } from "../../stores/i18nStore";
import { NotificationBell } from "../notifications/NotificationBell";
import { log } from "@/lib/log";

export interface BuildInfo {
  buildNumber?: number;
  version: string;
  buildDate: string;
  gitCommit?: string;
  environment?: string;
}

/** Remembers whether the desktop sidebar is folded down to its icon rail. */
const RAIL_STORAGE_KEY = "healthpass.sidebar.collapsed";

function readRailCollapsed(): boolean {
  try {
    return localStorage.getItem(RAIL_STORAGE_KEY) === "1";
  } catch {
    // Private mode or blocked storage: start with the labelled sidebar.
    return false;
  }
}

const DashboardLayout: React.FC = () => {
  // Two states that used to be one. `sidebarOpen` both slid the phone drawer
  // in and widened the desktop sidebar, so the desktop started as an unlabelled
  // icon rail and folded back to it after every click. The drawer is now only
  // for accounts with more than the traveller's five destinations (staff,
  // partners); the desktop sidebar is labelled unless folded on purpose.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [railCollapsed, setRailCollapsed] = useState(readRailCollapsed);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [buildInfo, setBuildInfo] = useState<BuildInfo | null>(null);
  /** null until the reader opens or closes one; "" means all closed. */
  const [openSection, setOpenSection] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, logout } = useAuthStore();
  const { t } = useI18nStore();
  const {
    shouldShowTourOperatorManagement,
    shouldShowHealthcareProviderManagement,
    shouldShowAffiliatePartnerManagement,
  } = useRole();

  // Reminders keep to the traveller's own day, so the server needs their zone.
  useEffect(() => {
    if (token) void syncTimeZone();
  }, [token]);

  // Initialize avatar from user object
  useEffect(() => {
    if (user?.avatarUrl) {
      setAvatarUrl(user.avatarUrl);
    }
  }, [user?.avatarUrl]);

  // Listen for avatar updates from profile page
  useEffect(() => {
    const handleAvatarUpdate = (event: CustomEvent) => {
      setAvatarUrl(event.detail.avatarUrl);
    };

    window.addEventListener(
      "avatarUpdated",
      handleAvatarUpdate as EventListener,
    );

    return () => {
      window.removeEventListener(
        "avatarUpdated",
        handleAvatarUpdate as EventListener,
      );
    };
  }, []);

  // Load build info
  useEffect(() => {
    fetch("/build-info.json")
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        log.debug("Build info loaded:", data);
        setBuildInfo(data);
      })
      .catch((err) => {
        console.error("Failed to load build info:", err);
        // Set fallback build info
        setBuildInfo({
          buildNumber: 203,
          version: "1.0.203",
          buildDate: new Date().toISOString(),
          environment: "development",
        });
      });
  }, []);

  const handleLogout = async () => {
    // Awaited so the login page never renders over data still being cleared.
    await logout();
    navigate("/traveler/login");
  };

  const toggleRail = () => {
    setRailCollapsed((collapsed) => {
      const next = !collapsed;
      try {
        localStorage.setItem(RAIL_STORAGE_KEY, next ? "1" : "0");
      } catch {
        // Not remembered across visits; the toggle still works.
      }
      return next;
    });
  };

  // What every account gets: this person's own travel health, as the five
  // destinations of src/config/travelerNav.ts. A staff account is also a
  // traveller account, so these stay reachable for staff at their URLs.
  const currentDestination = destinationFor(location.pathname);
  const onEmergency = isEmergencyPath(location.pathname);

  // Partner management, for partners. These are profile roles, not staff roles
  // — a tour operator running their own tours is not back-office staff — so
  // they stay in the ordinary menu rather than moving into the back office.
  const partnerMenuItems = [
    ...(shouldShowTourOperatorManagement
      ? [{ icon: Plane, label: "Tour Management", path: "/admin/tours" }]
      : []),
    ...(shouldShowHealthcareProviderManagement
      ? [
          {
            icon: Heart,
            label: "Healthcare Provider",
            path: "/admin/healthcare",
          },
        ]
      : []),
    ...(shouldShowAffiliatePartnerManagement
      ? [
          {
            icon: Shield,
            label: "Affiliate Partner",
            path: "/admin/affiliates",
          },
        ]
      : []),
  ];

  // The back office, per REQ-015 AC-005a: eight sections, filtered to what the
  // signed-in realm roles permit.
  const sections = sectionsForRoles(rolesFromToken(token));
  // ComDesk's surfaces come from ComDesk itself once its manifest has loaded.
  const comdeskMenu = useComDeskMenu(
    sections.some((section) => section.id === "comdesk"),
  );
  const backOffice = comdeskMenu
    ? sections.map((section) =>
        section.id === "comdesk" ? { ...section, pages: comdeskMenu } : section,
      )
    : sections;

  // Staff see the back office and nothing else. Their own travel-health pages
  // stay reachable at their /traveler URLs, but someone signed in to work does
  // not want nine personal health links above the tools they came for
  // (REQ-015 AC-005).
  const isStaff = backOffice.length > 0;

  // The drawer and its menu button exist only for accounts with more to reach
  // than the bottom tab bar holds.
  const hasDrawer = isStaff || partnerMenuItems.length > 0;

  // Class fragments for the desktop rail: labels hide and items centre only
  // from lg up, so the phone drawer always shows labels.
  const railItem = railCollapsed ? "lg:justify-center lg:px-0" : "";
  const railLabel = railCollapsed ? "lg:hidden" : "";

  return (
    <div className="min-h-screen bg-ground">
      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-sm border-b border-gray-200">
        <div className="flex items-center justify-between px-4 py-3">
          {/* Left Section: Hamburger Menu + Logo */}
          <div className="flex items-center space-x-4">
            {/* Phone: the drawer, for staff and partners only */}
            {hasDrawer && (
              <button
                onClick={() => setDrawerOpen(!drawerOpen)}
                aria-label={drawerOpen ? "Close navigation" : "Open navigation"}
                aria-expanded={drawerOpen}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors lg:hidden"
              >
                {drawerOpen ? (
                  <X className="w-6 h-6 text-gray-600" />
                ) : (
                  <Menu className="w-6 h-6 text-gray-600" />
                )}
              </button>
            )}

            {/* Desktop: fold the sidebar to its icon rail and back */}
            <button
              onClick={toggleRail}
              aria-label={railCollapsed ? t.navExpandMenu : t.navCollapseMenu}
              aria-expanded={!railCollapsed}
              className="hidden p-2 rounded-lg hover:bg-gray-100 transition-colors lg:inline-flex"
            >
              {railCollapsed ? (
                <PanelLeftOpen className="w-6 h-6 text-gray-600" />
              ) : (
                <PanelLeftClose className="w-6 h-6 text-gray-600" />
              )}
            </button>

            {/* HealthPass Logo */}
            <div className="flex items-center">
              {/* shrink-0 is load-bearing: the height is fixed, so without it
                  the image is a flex item that gives up width when the header
                  runs out of room on a phone. It does not scale down - it
                  squashes, and the round dots in the mark become ovals.
                  Smaller on a narrow screen so there is room to keep. */}
              <img
                src="/images/healthpass_logo.png"
                alt="HealthPass Logo"
                className="h-8 w-auto shrink-0 object-contain sm:h-12"
              />
            </div>
          </div>

          {/* Right Section: Sync Status + Notifications + User Avatar & Info */}
          <div className="flex items-center gap-4 relative">
            {/* Connection + offline queue state */}
            <SyncStatusIndicator />

            {/* Phone: Emergency one tap away on every screen; the sidebar
                carries it on desktop */}
            {!isStaff && (
              <Link
                to={EMERGENCY_PATH}
                aria-label={t.emergency}
                aria-current={onEmergency ? "page" : undefined}
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-emergency text-white lg:hidden"
              >
                <ShieldPlus className="h-5 w-5" aria-hidden="true" />
              </Link>
            )}

            {/* Notification Bell */}
            <NotificationBell />

            {user && (
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-3 hover:bg-gray-50 rounded-lg p-2 transition-colors"
                >
                  {avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={`${user.firstName} ${user.lastName}`}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-gradient-to-r from-sky-500 to-green-500 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-white" />
                    </div>
                  )}
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-semibold text-gray-900">
                      {user.firstName} {user.lastName}
                    </p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                  </div>
                  <ChevronDown
                    className={`w-4 h-4 text-gray-500 transition-transform ${
                      userMenuOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {/* User Dropdown Menu */}
                {userMenuOpen && (
                  <>
                    {/* Backdrop */}
                    <div
                      className="fixed inset-0 z-40"
                      role="button"
                      tabIndex={0}
                      onClick={() => setUserMenuOpen(false)}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") {
                          setUserMenuOpen(false);
                        }
                      }}
                      aria-label="Close menu"
                    />
                    {/* Menu */}
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        right: 0,
                        marginTop: "8px",
                        width: "220px",
                        background: "var(--ui-glass)",
                        backdropFilter: "blur(20px)",
                        borderRadius: "12px",
                        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
                        border: "1px solid rgba(0, 0, 0, 0.08)",
                        overflow: "hidden",
                        zIndex: 50,
                      }}
                    >
                      <div style={{ padding: "8px" }}>
                        <button
                          onClick={() => {
                            setUserMenuOpen(false);
                            navigate("/traveler/profile");
                          }}
                          style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            padding: "12px",
                            borderRadius: "8px",
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            transition: "background 0.2s ease",
                            textAlign: "left",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background =
                              "rgba(59, 130, 246, 0.1)")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = "transparent")
                          }
                        >
                          <User
                            style={{
                              width: "18px",
                              height: "18px",
                              color: "var(--ui-link)",
                            }}
                          />
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: "500",
                              color: "var(--ui-text-strong)",
                            }}
                          >
                            {t.profile}
                          </span>
                        </button>

                        <button
                          onClick={() => {
                            setUserMenuOpen(false);
                            navigate("/traveler/settings");
                          }}
                          style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            padding: "12px",
                            borderRadius: "8px",
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            transition: "background 0.2s ease",
                            textAlign: "left",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background =
                              "rgba(59, 130, 246, 0.1)")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = "transparent")
                          }
                        >
                          <Bell
                            style={{
                              width: "18px",
                              height: "18px",
                              color: "var(--ui-text-muted)",
                            }}
                          />
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: "500",
                              color: "var(--ui-text-strong)",
                            }}
                          >
                            {t.navNotifications}
                          </span>
                        </button>

                        <button
                          onClick={() => {
                            setUserMenuOpen(false);
                            navigate("/traveler/settings?tab=appearance");
                          }}
                          style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            padding: "12px",
                            borderRadius: "8px",
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            transition: "background 0.2s ease",
                            textAlign: "left",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background =
                              "rgba(59, 130, 246, 0.1)")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = "transparent")
                          }
                        >
                          <Award
                            style={{
                              width: "18px",
                              height: "18px",
                              color: "#8b5cf6",
                            }}
                          />
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: "500",
                              color: "var(--ui-text-strong)",
                            }}
                          >
                            {t.appearance}
                          </span>
                        </button>

                        <button
                          onClick={() => {
                            setUserMenuOpen(false);
                            navigate("/traveler/settings?tab=security");
                          }}
                          style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            padding: "12px",
                            borderRadius: "8px",
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            transition: "background 0.2s ease",
                            textAlign: "left",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background =
                              "rgba(59, 130, 246, 0.1)")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = "transparent")
                          }
                        >
                          <Lock
                            style={{
                              width: "18px",
                              height: "18px",
                              color: "#22c55e",
                            }}
                          />
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: "500",
                              color: "var(--ui-text-strong)",
                            }}
                          >
                            {t.security}
                          </span>
                        </button>

                        <button
                          onClick={() => {
                            setUserMenuOpen(false);
                            navigate("/app-info");
                          }}
                          style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            padding: "12px",
                            borderRadius: "8px",
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            transition: "background 0.2s ease",
                            textAlign: "left",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background =
                              "rgba(59, 130, 246, 0.1)")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = "transparent")
                          }
                          data-testid="menu-app-info"
                        >
                          <Info
                            style={{
                              width: "18px",
                              height: "18px",
                              color: "#0ea5e9",
                            }}
                          />
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: "500",
                              color: "var(--ui-text-strong)",
                            }}
                          >
                            App Info
                          </span>
                        </button>

                        <div
                          style={{
                            height: "1px",
                            background: "var(--ui-line-soft)",
                            margin: "8px 0",
                          }}
                        />

                        <button
                          onClick={() => {
                            setUserMenuOpen(false);
                            void handleLogout();
                          }}
                          style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            padding: "12px",
                            borderRadius: "8px",
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            transition: "background 0.2s ease",
                            textAlign: "left",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.background =
                              "rgba(239, 68, 68, 0.1)")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.background = "transparent")
                          }
                        >
                          <LogOut
                            style={{
                              width: "18px",
                              height: "18px",
                              color: "#ef4444",
                            }}
                          />
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: "500",
                              color: "#ef4444",
                            }}
                          >
                            {t.logout}
                          </span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {drawerOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          role="button"
          tabIndex={0}
          onClick={() => setDrawerOpen(false)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setDrawerOpen(false);
            }
          }}
          aria-label="Close sidebar"
        />
      )}

      {/* Slide-out Menu – Mobile: drawer for staff and partners,
          Desktop: persistent, labelled unless folded to the icon rail.
          `invisible` keeps the closed drawer's links out of the tab order. */}
      <aside
        className={`
        fixed top-16 left-0 h-[calc(100vh-4rem)] w-80 bg-white shadow-xl z-50
        transform transition-all duration-300 ease-in-out
        ${drawerOpen ? "translate-x-0 visible" : "-translate-x-full invisible"}
        lg:visible lg:translate-x-0 lg:shadow-none lg:border-r lg:border-border
        ${railCollapsed ? "lg:w-16" : "lg:w-60"}
      `}
      >
        <div className="flex flex-col h-full overflow-hidden">
          {/* Navigation Menu */}
          <nav className="flex-1 overflow-y-auto py-4">
            {isStaff ? (
              <div className="space-y-1">
                {backOffice.map((section) => {
                  const Icon = section.icon;
                  const owns = (path: string) =>
                    location.pathname === path ||
                    location.pathname.startsWith(path + "/");
                  // The base path counts too, so a URL typed or bookmarked at
                  // the section root highlights the section it belongs to
                  // rather than nothing.
                  const isCurrent =
                    owns(sectionBasePath(section)) ||
                    section.pages.some((page) => owns(page.path));
                  // The section holding the current page starts open, so a
                  // reload never hides where you are.
                  const isOpen =
                    openSection === section.id ||
                    (openSection === null && isCurrent);
                  // Where the header goes. Null only for a section whose pages
                  // are all still unbuilt, which has nowhere honest to land.
                  const landing =
                    section.pages.length === 0
                      ? (section.path ?? null)
                      : sectionLandingPath(section);

                  return (
                    <div key={section.id}>
                      <button
                        onClick={() => {
                          // Opening a section also goes there. The header used
                          // to expand and nothing else, so a section looked
                          // clickable and led nowhere — the complaint behind
                          // this change. Collapsing an open section is still
                          // just a collapse: you are already on one of its
                          // pages, so there is nothing to navigate to.
                          if (isOpen && section.pages.length > 0) {
                            setOpenSection("");
                            return;
                          }
                          setOpenSection(section.id);
                          if (landing) {
                            navigate(landing);
                            // The menu stays open, unlike a page link: the
                            // header has just revealed the section's pages and
                            // closing over them would undo the expansion.
                            if (section.pages.length === 0) {
                              setDrawerOpen(false);
                            }
                          }
                        }}
                        title={railCollapsed ? section.label : undefined}
                        className={`
                          w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors
                          ${railItem}
                          ${
                            isCurrent
                              ? "bg-primary-50 text-primary-700 font-semibold"
                              : "text-muted-foreground hover:bg-muted"
                          }
                        `}
                      >
                        <Icon className="w-5 h-5 flex-shrink-0" />
                        <span
                          className={`flex-1 font-medium whitespace-nowrap ${railLabel}`}
                        >
                          {section.label}
                        </span>
                        {section.pages.length > 0 && (
                          <ChevronDown
                            className={`w-4 h-4 flex-shrink-0 transition-transform ${
                              isOpen ? "rotate-180" : ""
                            } ${railLabel}`}
                          />
                        )}
                      </button>

                      {/* Pages that are specified but not built are shown
                          disabled rather than left out (REQ-015 AC-005a), so
                          what is still missing is readable off the menu. */}
                      {isOpen && (drawerOpen || !railCollapsed) && (
                        <div className="mt-1 mb-2 space-y-0.5">
                          {section.pages.map((page) =>
                            page.available ? (
                              <button
                                key={page.path}
                                onClick={() => {
                                  navigate(page.path);
                                  setDrawerOpen(false);
                                }}
                                className={`w-full flex items-center py-2 pl-12 pr-4 rounded-lg text-left text-sm transition-colors ${
                                  owns(page.path)
                                    ? "bg-primary-50 text-primary-700 font-medium"
                                    : "text-muted-foreground hover:bg-muted"
                                }`}
                              >
                                {page.label}
                              </button>
                            ) : (
                              <div
                                key={page.path}
                                title={`Specified in ${section.specifiedBy}, not built yet`}
                                className="w-full flex items-center justify-between py-2 pl-12 pr-4 text-sm text-gray-400 cursor-default"
                              >
                                <span>{page.label}</span>
                                <span className="text-[10px] uppercase tracking-wide text-gray-300">
                                  soon
                                </span>
                              </div>
                            ),
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-1 px-2">
                {DESTINATIONS.map((destination) => {
                  const Icon = destination.icon;
                  const label = destination.label(t);
                  const isActive = currentDestination?.id === destination.id;

                  return (
                    <Link
                      key={destination.id}
                      to={destination.path}
                      onClick={() => setDrawerOpen(false)}
                      aria-current={isActive ? "page" : undefined}
                      title={railCollapsed ? label : undefined}
                      className={`
                      w-full flex items-center gap-3 px-4 min-h-[46px] rounded-xl text-left transition-colors
                      ${railItem}
                      ${
                        isActive
                          ? "bg-primary-50 text-primary-700 font-bold"
                          : "text-muted-foreground font-semibold hover:bg-muted"
                      }
                    `}
                    >
                      <Icon
                        className="w-5 h-5 flex-shrink-0"
                        aria-hidden="true"
                      />
                      <span className={`whitespace-nowrap ${railLabel}`}>
                        {label}
                      </span>
                    </Link>
                  );
                })}

                {partnerMenuItems.length > 0 && (
                  <div className="my-3 border-t border-border" />
                )}
                {partnerMenuItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = isUnder(location.pathname, item.path);

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setDrawerOpen(false)}
                      aria-current={isActive ? "page" : undefined}
                      title={railCollapsed ? item.label : undefined}
                      className={`
                      w-full flex items-center gap-3 px-4 min-h-[46px] rounded-xl text-left transition-colors
                      ${railItem}
                      ${
                        isActive
                          ? "bg-primary-50 text-primary-700 font-bold"
                          : "text-muted-foreground font-semibold hover:bg-muted"
                      }
                    `}
                    >
                      <Icon
                        className="w-5 h-5 flex-shrink-0"
                        aria-hidden="true"
                      />
                      <span className={`whitespace-nowrap ${railLabel}`}>
                        {item.label}
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}
          </nav>

          {/* Emergency, apart from the destinations and always in reach */}
          {!isStaff && (
            <div className="px-2 pb-3">
              <Link
                to={EMERGENCY_PATH}
                onClick={() => setDrawerOpen(false)}
                aria-current={onEmergency ? "page" : undefined}
                title={railCollapsed ? t.emergency : undefined}
                className={`flex items-center gap-3 px-4 min-h-[48px] rounded-xl bg-emergency text-white font-bold ${railItem} ${
                  onEmergency ? "ring-4 ring-red-200" : ""
                }`}
              >
                <ShieldPlus
                  className="w-5 h-5 flex-shrink-0"
                  aria-hidden="true"
                />
                <span className={`whitespace-nowrap ${railLabel}`}>
                  {t.emergency}
                </span>
              </Link>
            </div>
          )}

          {/* Build Number / Version Info */}
          <div className="border-t border-gray-200">
            {buildInfo ? (
              <button
                onClick={() => {
                  navigate("/app-info");
                  setDrawerOpen(false);
                }}
                title={
                  railCollapsed
                    ? `v${buildInfo.version} #${buildInfo.buildNumber}`
                    : undefined
                }
                className={`w-full flex items-center justify-between px-4 py-3 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors
                  ${railItem}`}
              >
                <div className="flex items-center gap-3">
                  <Package className="w-5 h-5 text-sky-600 flex-shrink-0" />
                  <div className={`text-left ${railLabel}`}>
                    <div className="text-xs text-gray-500">
                      {t.version || "Version"}
                    </div>
                    <div className="font-medium text-sm">
                      v{buildInfo.version}
                    </div>
                  </div>
                </div>
                <div
                  className={`text-xs bg-primary-50 text-primary-700 px-2 py-1 rounded ${railLabel}`}
                >
                  #{buildInfo.buildNumber}
                </div>
              </button>
            ) : (
              <div
                className={`w-full flex items-center gap-3 px-4 py-3 text-gray-400 ${railItem}`}
              >
                <Package className="w-5 h-5 flex-shrink-0" />
                <span className={`font-medium text-sm ${railLabel}`}>
                  {t.loading || "Laden..."}
                </span>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content – offset on desktop based on sidebar width, and clear
          of the bottom tab bar on a phone */}
      <main
        className={`pt-16 transition-all duration-300 ease-in-out ${railCollapsed ? "lg:ml-16" : "lg:ml-60"} ${
          isStaff ? "" : "pb-[calc(96px+env(safe-area-inset-bottom))] lg:pb-0"
        }`}
      >
        {/* ComDesk fills the whole content area, edge to edge
            (pages/admin/comdesk/ComDeskFrame.tsx). */}
        <div
          className={
            location.pathname.startsWith("/admin/comdesk/")
              ? ""
              : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
          }
        >
          {!isStaff && <SectionTabs />}
          <Outlet />
        </div>
      </main>

      {!isStaff && <BottomTabBar />}
    </div>
  );
};

export default DashboardLayout;
